import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CheckCircle,
  CloudQueue,
} from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import { useAuth } from '../contexts/GoogleAuthContext';
import ConfirmationDialog from '../components/ConfirmationDialog';
import NotificationSnackbar from '../components/NotificationSnackbar';
import { api } from '../services/api-client';
import { PermissionChange } from '../services/api';
// Permission changes will be handled via API

interface Service {
  name: string;
  url: string;
  type: string;
  description: string;
  health: 'healthy' | 'unhealthy' | 'unknown';
}

interface AgentPermissions {
  [agentName: string]: string[];
}

export default function ServiceRegistry() {
  const { user } = useAuth();
  const canManagePermissions = user?.role === 'admin' || user?.role === 'developer';
  const [permissions, setPermissions] = useState<AgentPermissions>({});
  const [originalPermissions, setOriginalPermissions] = useState<AgentPermissions>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Fetch services
  const { data: servicesData, isLoading: servicesLoading, error: servicesError } = useQuery(
    'services',
    () => api.getServices(),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 2,
      onError: (error) => {
        console.error('Failed to fetch services:', error);
      }
    }
  );
  const services = servicesData?.services || [];

  // Fetch agent permissions
  const { data: agentData, isLoading: permissionsLoading, error: permissionsError } = useQuery(
    'agentPermissions',
    () => api.getAgentPermissions(),
    {
      retry: 2,
      onError: (error) => {
        console.error('Failed to fetch agent permissions:', error);
      }
    }
  );

  useEffect(() => {
    if (agentData) {
      // Extract permissions from the response
      const perms = agentData.permissions || agentData;
      // Transform to ensure each agent has an array of services
      const transformedPerms: AgentPermissions = {};
      Object.entries(perms).forEach(([agent, data]: [string, any]) => {
        if (Array.isArray(data)) {
          transformedPerms[agent] = data;
        } else if (data && Array.isArray(data.services)) {
          transformedPerms[agent] = data.services;
        } else {
          transformedPerms[agent] = [];
        }
      });
      setPermissions(transformedPerms);
      setOriginalPermissions(transformedPerms);
    }
  }, [agentData]);

  // Check if there are changes
  useEffect(() => {
    const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
    setHasChanges(changed);
  }, [permissions, originalPermissions]);

  const handlePermissionChange = (agent: string, service: string, checked: boolean) => {
    if (!canManagePermissions) return;

    setPermissions(prev => {
      const current = prev[agent] || [];
      if (checked) {
        return { ...prev, [agent]: [...current, service] };
      } else {
        return { ...prev, [agent]: current.filter(s => s !== service) };
      }
    });
  };

  const handleSave = (agent: string) => {
    setSelectedAgent(agent);
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (!selectedAgent) return;

    const original = originalPermissions[selectedAgent] || [];
    const current = permissions[selectedAgent] || [];

    const added = current.filter(s => !original.includes(s));
    const removed = original.filter(s => !current.includes(s));

    const change: PermissionChange = {
      agent: selectedAgent,
      services: current,
      changes: { added, removed },
      requestedBy: user!.email,
      type: 'bulk_update'
    };

    try {
      // Update permissions via API
      await api.updateAgentPermissions(selectedAgent, current);

      setShowConfirmDialog(false);

      // Reset the local changes since we're waiting for email confirmation
      setPermissions(prev => ({
        ...prev,
        [selectedAgent]: originalPermissions[selectedAgent] || []
      }));

      // Show success message
      setSnackbar({
        open: true,
        message: 'Confirmation email sent! Please check your inbox to complete the changes.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to create change request:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send confirmation email. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCancel = (agent: string) => {
    setPermissions(prev => ({
      ...prev,
      [agent]: originalPermissions[agent] || []
    }));
  };

  if (servicesLoading || permissionsLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Service Registry</Typography>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  // Handle errors
  if (servicesError || permissionsError) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CloudQueue color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">Service Registry</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage which MCP tools each agent can access
            </Typography>
          </Box>
        </Box>
        <Alert severity="error">
          <Typography variant="body2" fontWeight="bold">Unable to Load Service Registry</Typography>
          <Typography variant="body2">
            {servicesError ? `Services: ${(servicesError as Error).message}` : ''}
            {servicesError && permissionsError ? ' | ' : ''}
            {permissionsError ? `Permissions: ${(permissionsError as Error).message}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please ensure the service registry is running and try refreshing the page.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const agents = Object.keys(permissions).filter(agent => agent !== 'permissions');
  const mpcServices = services?.filter(s => s.type === 'mcp_server') || [];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CloudQueue color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4">Service Registry</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage which MCP tools each agent can access
          </Typography>
        </Box>
      </Box>

      {!canManagePermissions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access. Contact an admin to make changes.
        </Alert>
      )}

      {(!services || services.length === 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">No Services Found</Typography>
          <Typography variant="body2">
            The service registry is empty. Deploy MCP services to see them here.
          </Typography>
        </Alert>
      )}

      {agents.map(agent => {
        const agentPermissions = permissions[agent] || [];
        const originalAgentPermissions = originalPermissions[agent] || [];
        const agentHasChanges = JSON.stringify(agentPermissions.sort()) !==
                               JSON.stringify(originalAgentPermissions.sort());

        return (
          <Card key={agent} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">{agent}</Typography>
                  {agentHasChanges && (
                    <Chip label="Modified" size="small" color="warning" />
                  )}
                </Box>
                {canManagePermissions && agentHasChanges && (
                  <Box>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      onClick={() => handleSave(agent)}
                      sx={{ mr: 1 }}
                    >
                      Save Changes
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      onClick={() => handleCancel(agent)}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Service</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mpcServices.map(service => (
                      <TableRow key={service.name}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={agentPermissions.includes(service.name)}
                            onChange={(e) => handlePermissionChange(agent, service.name, e.target.checked)}
                            disabled={!canManagePermissions}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {service.name}
                            <Tooltip title={service.url}>
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>{service.description}</TableCell>
                        <TableCell>
                          <Chip
                            icon={<CheckCircle />}
                            label={service.active !== false ? 'active' : 'inactive'}
                            size="small"
                            color={service.active !== false ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })}

      {showConfirmDialog && (
        <ConfirmationDialog
          open={true}
          agent={selectedAgent}
          changes={{
            agent: selectedAgent,
            added: (permissions[selectedAgent] || []).filter(
              s => !(originalPermissions[selectedAgent] || []).includes(s)
            ),
            removed: (originalPermissions[selectedAgent] || []).filter(
              s => !(permissions[selectedAgent] || []).includes(s)
            )
          }}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}
